/**
 * AddPoleDialog Component - Dialog for adding new poles
 */
import React, { useState, useEffect } from 'react';
import { usePoleStore, useProjectStore } from '../../stores';
import { useONUStore } from '../../stores/onuStore';
import { useCustomerStore } from '../../stores/customerStore';
import { useSplitterCatalogStore } from '../../stores/splitterCatalogStore';
import type { AvailablePortsResponse } from '../../types';
import { getErrorMessage } from '../../utils/errorMessages';
import './Dialog.css';

interface AddPoleDialogProps {
  isOpen: boolean;
  onClose: () => void;
  latitude: number;
  longitude: number;
}

export const AddPoleDialog: React.FC<AddPoleDialogProps> = ({
  isOpen,
  onClose,
  latitude,
  longitude,
}) => {
  const { currentProject } = useProjectStore();
  const { createPole, poles } = usePoleStore();
  const { createONU, getAvailablePorts, onus } = useONUStore();
  const { customers, fetchCustomers } = useCustomerStore();

  const [name, setName] = useState('');
  const [poleType, setPoleType] = useState<string>('pole');
  const [splitterType, setSplitterType] = useState<string>('');
  const splitterCatalog = useSplitterCatalogStore((s) => s.types).filter((t) => t.is_active !== false);
  const [endpointType, setEndpointType] = useState<string>('none');
  const [customerCount, setCustomerCount] = useState<number>(0);
  const [oltPortCount, setOltPortCount] = useState<number>(0);
  const [notes, setNotes] = useState('');
  // Podporný stĺp: kábel je naň len upnutý, prechádza neprerušený, kazeta
  // tam nie je. Neskôr sa dá krabica s kazetou pridať — stačí prepnúť.
  // Pridané 2026-09-30.
  const [bezKazety, setBezKazety] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ONU specific fields
  const [onuPoleId, setOnuPoleId] = useState<number>(0);
  const [onuCustomerOption, setOnuCustomerOption] = useState<'unassigned' | 'existing' | 'new'>('unassigned');
  const [onuCustomerId, setOnuCustomerId] = useState<number | undefined>(undefined);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [onuModel, setOnuModel] = useState('');
  const [onuSerialNumber, setOnuSerialNumber] = useState('');
  const [onuMacAddress, setOnuMacAddress] = useState('');
  const [onuFiberColor, setOnuFiberColor] = useState('');
  const [onuSplitterIndex, setOnuSplitterIndex] = useState<number | undefined>(undefined);
  const [onuSplitterPort, setOnuSplitterPort] = useState<number | undefined>(undefined);
  const [availablePorts, setAvailablePorts] = useState<AvailablePortsResponse | null>(null);
  const [loadingPorts, setLoadingPorts] = useState(false);

  // Filter poles with customer splitters for ONU
  const polesWithCustomerSplitters = poles.filter(
    (pole) => pole.cassette?.splitters && pole.cassette.splitters.some(s => s.is_customer_splitter)
  );

  // Filter active customers
  const activeCustomers = customers.filter(c => c.status === 'active' || c.status === 'pending_installation');

  // Load customers when dialog opens (customers are now global)
  useEffect(() => {
    if (isOpen) {
      fetchCustomers();
    }
  }, [isOpen]);

  // Load available ports when pole is selected for ONU
  useEffect(() => {
    if (poleType === 'onu' && onuPoleId && onuPoleId > 0) {
      loadAvailablePorts(onuPoleId);
    } else {
      setAvailablePorts(null);
    }
  }, [poleType, onuPoleId]);

  // Auto-generate ONU name based on customer selection
  useEffect(() => {
    if (poleType === 'onu') {
      if (onuCustomerOption === 'existing' && onuCustomerId) {
        const customer = customers.find(c => c.id === onuCustomerId);
        if (customer) {
          let customerName = '';
          if (customer.customer_type === 'individual') {
            customerName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
          } else {
            customerName = customer.company_name || 'Firma';
          }

          // Count existing ONUs for this customer
          const customerONUs = onus.filter(onu => onu.customer_id === onuCustomerId);

          if (customerONUs.length === 0) {
            // First ONU for this customer
            setName(customerName);
          } else {
            // Customer already has ONU(s), add number
            setName(`${customerName} ${customerONUs.length + 1}`);
          }
        }
      } else if (onuCustomerOption === 'unassigned') {
        setName('Nepriradené');
      } else if (onuCustomerOption === 'new') {
        setName('Nový zákazník');
      }
    }
  }, [poleType, onuCustomerOption, onuCustomerId, customers, onus]);

  const loadAvailablePorts = async (poleId: number) => {
    setLoadingPorts(true);
    try {
      const ports = await getAvailablePorts(poleId);
      setAvailablePorts(ports);
    } catch (error) {
      console.error('Failed to load available ports:', error);
      setAvailablePorts(null);
    } finally {
      setLoadingPorts(false);
    }
  };

  // Skryť bottom navigation na mobile, keď je dialóg otvorený
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('dialog-open');
    } else {
      document.body.classList.remove('dialog-open');
    }
    return () => {
      document.body.classList.remove('dialog-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) e.preventDefault();
    if (!currentProject) return;

    setLoading(true);
    setError(null);

    try {
      // Handle ONU creation
      if (poleType === 'onu') {
        if (!onuPoleId || onuPoleId === 0) {
          setError('Vyberte stĺp so splittrom');
          setLoading(false);
          return;
        }

        // Handle new customer creation
        if (onuCustomerOption === 'new') {
          setShowNewCustomerForm(true);
          setLoading(false);
          return;
        }

        // Determine customer_id
        let customerId: number | undefined = undefined;
        if (onuCustomerOption === 'existing' && onuCustomerId) {
          customerId = onuCustomerId;
        }

        await createONU({
          project_id: currentProject.id,
          pole_id: onuPoleId,
          customer_id: customerId,
          name,
          latitude,
          longitude,
          onu_model: onuModel || undefined,
          serial_number: onuSerialNumber || undefined,
          mac_address: onuMacAddress || undefined,
          splitter_index: onuSplitterIndex,
          splitter_port: onuSplitterPort,
          fiber_color: onuFiberColor || undefined,
          status: 'pending',
          notes: notes || undefined,
        });
        handleClose();
        return;
      }

      // Handle Pole creation
      const poleData: any = {
        name,
        pole_type: poleType,
        latitude,
        longitude,
        endpoint_type: endpointType,
        notes: notes || undefined,
      };

      if (poleType === 'splitter_point' && splitterType) {
        poleData.splitter_type = splitterType;
      }

      if (endpointType === 'building' && customerCount > 0) {
        poleData.customer_count = customerCount;
      }

      if (poleType === 'olt' && oltPortCount > 0) {
        poleData.olt_port_count = oltPortCount;
      }

      // Podporný stĺp — zámerne bez kazety. Zapíšeme to výslovne, aby sa dal
      // odlíšiť od stĺpa, ktorý kazetu len ešte nemá nastavenú.
      if (bezKazety) {
        poleData.cassette = {
          has_cassette: false,
          pass_through_cables: [],
          splices: [],
          splitters: [],
        };
      }

      await createPole(currentProject.id, poleData);
      handleClose();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Nepodarilo sa vytvoriť stĺpik.'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setPoleType('pole');
    setSplitterType('');
    setEndpointType('none');
    setCustomerCount(0);
    setOltPortCount(0);
    setNotes('');
    setOnuPoleId(0);
    setOnuCustomerOption('unassigned');
    setOnuCustomerId(undefined);
    setShowNewCustomerForm(false);
    setOnuModel('');
    setOnuSerialNumber('');
    setOnuMacAddress('');
    setOnuSplitterIndex(undefined);
    setOnuSplitterPort(undefined);
    setAvailablePorts(null);
    setError(null);
    onClose();
  };

  return (
    <div className="dialog-overlay" onClick={handleClose}>
      <div className="dialog" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h2>Pridať nový stĺpik</h2>
          <button className="dialog-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <div className="dialog-content">
          {error && <div className="dialog-error">{error}</div>}

          {poleType !== 'onu' && (
            <div className="form-group">
              <label>Názov *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="napr. Stĺpik-01, OLT-Centrála"
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Typ stĺpika *</label>
            <select
              value={poleType}
              onChange={(e) => setPoleType(e.target.value)}
              required
            >
              <option value="pole">Stĺpik (~1 m, skrinka pre zemnú inštaláciu)</option>
              <option value="manhole">🕳️ Šachta</option>
              <option value="utility_pole">Stĺp (elektrikársky, vzdušné vedenie)</option>
              <option value="olt">OLT</option>
              <option value="splitter_point">Bod rozdelenia</option>
              <option value="building">Budova</option>
              <option value="onu">📡 ONU (Koncové zariadenie)</option>
            </select>
          </div>

          {/* Podporný stĺp — kábel len upnutý, bez kazety */}
          {(poleType === 'pole' || poleType === 'manhole' || poleType === 'utility_pole') && (
            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bezKazety}
                  onChange={(e) => setBezKazety(e.target.checked)}
                />
                <span>Podporný — kábel je len upnutý, bez kazety</span>
              </label>
              <small className="form-hint">
                {bezKazety
                  ? 'Kábel prechádza neprerušený, nevzniká zvar. Krabicu s kazetou sa dá pridať aj neskôr.'
                  : 'Nechaj nezaškrtnuté, ak tu bude krabica s kazetou a kábel sa bude zvárať.'}
              </small>
            </div>
          )}

          {poleType === 'onu' && (
            <>
              {/* Generated ONU Name Display */}
              {name && (
                <div className="form-group">
                  <label>Názov ONU (automaticky generovaný)</label>
                  <div style={{
                    padding: '10px 12px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151'
                  }}>
                    📡 {name}
                  </div>
                </div>
              )}

              {/* Customer Selection */}
              <div className="form-group">
                <label>Zákazník</label>
                <select
                  value={onuCustomerOption}
                  onChange={(e) => {
                    setOnuCustomerOption(e.target.value as 'unassigned' | 'existing' | 'new');
                    setOnuCustomerId(undefined);
                  }}
                >
                  <option value="unassigned">🔘 Nepriradené (bez zákazníka)</option>
                  <option value="existing">👤 Vybrať existujúceho zákazníka</option>
                  <option value="new">➕ Vytvoriť nového zákazníka</option>
                </select>
              </div>

              {onuCustomerOption === 'existing' && (
                <div className="form-group">
                  <label>Vyberte zákazníka *</label>
                  <select
                    value={onuCustomerId || ''}
                    onChange={(e) => setOnuCustomerId(e.target.value ? parseInt(e.target.value) : undefined)}
                    required
                  >
                    <option value="">-- Vyberte zákazníka --</option>
                    {activeCustomers.map((customer) => {
                      const displayName = customer.customer_type === 'individual'
                        ? `${customer.first_name} ${customer.last_name}`
                        : customer.company_name;
                      return (
                        <option key={customer.id} value={customer.id}>
                          {displayName} ({customer.customer_number})
                        </option>
                      );
                    })}
                  </select>
                  {activeCustomers.length === 0 && (
                    <small className="form-hint" style={{ color: '#f59e0b' }}>
                      ⚠️ Žiadni aktívni zákazníci. Vytvorte najprv zákazníka.
                    </small>
                  )}
                </div>
              )}

              {onuCustomerOption === 'new' && (
                <div className="form-group">
                  <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #3b82f6' }}>
                    <p style={{ margin: 0, fontSize: '14px', color: '#1e40af' }}>
                      ℹ️ Po vytvorení ONU budete presmerovaný na vytvorenie nového zákazníka.
                    </p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Stĺp so zákazníckym splittrom *</label>
                <select
                  value={onuPoleId}
                  onChange={(e) => setOnuPoleId(parseInt(e.target.value))}
                  required
                >
                  <option value={0}>-- Vyberte stĺp --</option>
                  {polesWithCustomerSplitters.map((pole) => {
                    const customerSplitters = pole.cassette?.splitters?.filter(s => s.is_customer_splitter) || [];
                    return (
                      <option key={pole.id} value={pole.id}>
                        {pole.name} ({customerSplitters.length} zákaznícky{customerSplitters.length === 1 ? '' : 'ch'} splitter{customerSplitters.length === 1 ? '' : 'ov'})
                      </option>
                    );
                  })}
                </select>
                {polesWithCustomerSplitters.length === 0 && (
                  <small className="form-hint" style={{ color: '#ef4444' }}>
                    ⚠️ Žiadny stĺp nemá zákaznícky splitter. Najprv označte splitter ako zákaznícky v editore kazety.
                  </small>
                )}
              </div>

              {loadingPorts && (
                <div className="form-group">
                  <div style={{ padding: '12px', backgroundColor: '#f3f4f6', borderRadius: '6px' }}>
                    Načítavam porty...
                  </div>
                </div>
              )}

              {availablePorts && !loadingPorts && (
                <>
                  <div className="form-group">
                    <label>Splitter</label>
                    <select
                      value={onuSplitterIndex ?? ''}
                      onChange={(e) =>
                        setOnuSplitterIndex(e.target.value ? parseInt(e.target.value) : undefined)
                      }
                    >
                      <option value="">-- Vyberte splitter --</option>
                      {availablePorts.splitters
                        .filter(splitter => splitter.is_customer_splitter)
                        .map((splitter) => (
                          <option key={splitter.splitter_index} value={splitter.splitter_index}>
                            Splitter {splitter.splitter_index + 1} ({splitter.splitter_type}) - {splitter.available_ports}{' '}
                            voľných
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Port</label>
                    <select
                      value={onuSplitterPort ?? ''}
                      onChange={(e) =>
                        setOnuSplitterPort(e.target.value ? parseInt(e.target.value) : undefined)
                      }
                      disabled={onuSplitterIndex === undefined}
                    >
                      <option value="">-- Vyberte port --</option>
                      {onuSplitterIndex !== undefined &&
                        availablePorts.splitters[onuSplitterIndex]?.ports
                          .filter((port) => !port.is_used)
                          .map((port) => (
                            <option key={port.port_number} value={port.port_number}>
                              Port {port.port_number}
                            </option>
                          ))}
                    </select>
                  </div>
                </>
              )}

              <div className="form-group">
                <label>Model zariadenia</label>
                <input
                  type="text"
                  value={onuModel}
                  onChange={(e) => setOnuModel(e.target.value)}
                  placeholder="napr. Huawei HG8310M"
                />
              </div>

              <div className="form-group">
                <label>Sériové číslo</label>
                <input
                  type="text"
                  value={onuSerialNumber}
                  onChange={(e) => setOnuSerialNumber(e.target.value)}
                  placeholder="napr. HWTC12345678"
                />
              </div>

              <div className="form-group">
                <label>MAC adresa</label>
                <input
                  type="text"
                  value={onuMacAddress}
                  onChange={(e) => setOnuMacAddress(e.target.value)}
                  placeholder="napr. 00:11:22:33:44:55"
                />
              </div>

              <div className="form-group">
                <label>Farba vlákna</label>
                <select
                  value={onuFiberColor}
                  onChange={(e) => setOnuFiberColor(e.target.value)}
                >
                  <option value="">-- Vyberte farbu --</option>
                  <option value="blue">🔵 Modrá (Blue)</option>
                  <option value="orange">🟠 Oranžová (Orange)</option>
                  <option value="green">🟢 Zelená (Green)</option>
                  <option value="brown">🟤 Hnedá (Brown)</option>
                  <option value="gray">⚪ Sivá (Gray)</option>
                  <option value="white">⚪ Biela (White)</option>
                  <option value="red">🔴 Červená (Red)</option>
                  <option value="black">⚫ Čierna (Black)</option>
                  <option value="yellow">🟡 Žltá (Yellow)</option>
                  <option value="violet">🟣 Fialová (Violet)</option>
                  <option value="pink">🩷 Ružová (Pink)</option>
                  <option value="turquoise">🩵 Tyrkysová (Turquoise)</option>
                </select>
              </div>
            </>
          )}

          {poleType === 'splitter_point' && (
            <div className="form-group">
              <label>Typ splittra *</label>
              <select
                value={splitterType}
                onChange={(e) => setSplitterType(e.target.value)}
                required
              >
                <option value="">Vyberte typ splittra</option>
                {splitterCatalog.map((t) => (
                  <option key={t.id ?? t.name} value={t.name}>{t.name}</option>
                ))}
              </select>
            </div>
          )}

          {poleType === 'olt' && (
            <div className="form-group">
              <label>Počet OLT portov *</label>
              <input
                type="number"
                value={oltPortCount}
                onChange={(e) => setOltPortCount(parseInt(e.target.value) || 0)}
                min="1"
                max="64"
                placeholder="Počet OLT portov (1-64)"
                required
              />
              <small className="form-hint">
                Každý port sa môže pripojiť na jeden výstup vlákna
              </small>
            </div>
          )}

          {poleType !== 'onu' && (
            <>
              <div className="form-group">
                <label>Typ koncového bodu</label>
                <select
                  value={endpointType}
                  onChange={(e) => setEndpointType(e.target.value)}
                >
                  <option value="none">Žiadny</option>
                  <option value="single_customer">Jeden zákazník</option>
                  <option value="building">Budova</option>
                </select>
              </div>

              {endpointType === 'building' && (
                <div className="form-group">
                  <label>Počet zákazníkov</label>
                  <input
                    type="number"
                    value={customerCount}
                    onChange={(e) => setCustomerCount(parseInt(e.target.value) || 0)}
                    min="0"
                    placeholder="Počet zákazníkov"
                  />
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label>GPS súradnice</label>
            <div className="coordinates-display">
              <span>Lat: {latitude.toFixed(6)}</span>
              <span>Lng: {longitude.toFixed(6)}</span>
            </div>
          </div>

          <div className="form-group">
            <label>Poznámky</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Voliteľné poznámky..."
              rows={3}
            />
          </div>
        </div>

        <div className="dialog-actions">
          <button
            type="button"
            className="btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            Zrušiť
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? 'Vytvára sa...' : poleType === 'onu' ? 'Vytvoriť ONU' : 'Vytvoriť stĺpik'}
          </button>
        </div>
      </div>
    </div>
  );
};

